import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  usePropertyField,
  useTokens,
  SectionContextProvider,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { contrastRatio, getContrastTextColor, relativeLuminance } from '../utils/luminance';

// Google Maps embed key (optional — falls back to keyless embed when absent).
const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// Panel behind the lead form. Secondary so this last home section does not
// blend into the footer, which sits on `--vibe-color-primary`.
const PANEL_BG = 'var(--vibe-color-secondary)';

/** WCAG AA contrast for normal-size text. */
const WCAG_AA = 4.5;

/**
 * Prefer the theme on-color token when it already clears WCAG AA against the
 * panel. Otherwise synthesize white/black from the surface luminance so form
 * copy (and the move-in date placeholder) stay readable on light or dark
 * secondaries — same fallback SpecialOfferBanner / Header use.
 */
function pickReadableOnColor(
  surface: string | null | undefined,
  preferred: string | null | undefined,
): string | null {
  if (!surface) return null;
  if (preferred && contrastRatio(surface, preferred) >= WCAG_AA) return preferred;
  return relativeLuminance(surface) > 0.55 ? '#1a1a1a' : '#ffffff';
}

// ============================================================================
// Component
// ============================================================================

export function LetsConnect() {
  const formSlot = useBlockSlot('letsconnect.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const mapHeading = useContentToken('letsconnect.map_heading');

  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=15`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  // Luminance pick for copy + form fields on the secondary panel. Prefer
  // `textOnSecondary` when it meets WCAG AA; otherwise fall back to white or
  // near-black from the secondary's luminance (Header / SpecialOfferBanner).
  const tokens = useTokens();
  const onPanelColor = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return null;
    return pickReadableOnColor(secondary, tokens?.color?.textOnSecondary);
  }, [tokens]);

  // Primary CTA fill on the secondary panel. If primary fails AA against the
  // panel, swap the button fill to the luminance-picked on-panel color so the
  // CTA stays distinct and legible; button text then inverts to the panel.
  const [ctaFill, ctaText] = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    const primary = tokens?.color?.primary;
    if (!secondary || !primary) {
      return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    }
    if (contrastRatio(primary, secondary) >= WCAG_AA) {
      const onPrimary =
        pickReadableOnColor(primary, tokens?.color?.textOnPrimary) ??
        getContrastTextColor(primary);
      return [primary, onPrimary] as const;
    }
    const fill = onPanelColor ?? getContrastTextColor(secondary);
    return [fill, secondary] as const;
  }, [tokens, onPanelColor]);

  const panelStyle = {
    backgroundColor: PANEL_BG,
    ...(onPanelColor ? { ['--lc-on-panel' as string]: onPanelColor } : {}),
    ['--lc-cta-fill' as string]: ctaFill,
    ['--lc-cta-text' as string]: ctaText,
  };

  return (
    <section className="lc-section" aria-label={mapHeading as string | undefined}>
      <div className="lc-grid">
        {/* ── Left: dark panel with copy + lead form ── */}
        <div className="lc-left" style={panelStyle}>
          <div className="lc-inner">
            <RichTextContent
              path="letsconnect.heading"
              label="Heading"
              defaultValue="<p>Let's Connect</p>"
              as="h2"
              className="lc-heading"
            />

            <RichTextContent
              path="letsconnect.body"
              label="Intro Paragraph"
              defaultValue={
                '<p>Have questions about how to start living at your new home? Our dedicated leasing team at <span data-type="field-token" data-field="property_name"></span> is here to assist you.</p>'
              }
              as="div"
              className="lc-body"
            />

            <div className="lc-form-panel">
              <EditableToken path="letsconnect.form_block" as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <p className="lc-form-empty">No form block selected</p>
                )}
              </EditableToken>
            </div>
          </div>
        </div>

        {/* ── Right: location map ── */}
        <div className="lc-right">
          <EditableToken path="letsconnect.map_heading" as="h2" className="lc-sr-only">
            {mapHeading}
          </EditableToken>

          {mapSrc ? (
            <iframe
              className="lc-map"
              src={mapSrc}
              title={(mapHeading as string | undefined) || 'Property location map'}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="lc-map-placeholder">
              <p>Map location not available</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .lc-section { width: 100%; background: ${PANEL_BG}; }

        .lc-grid {
          display: grid;
          grid-template-columns: 1fr;
          align-items: stretch;
        }
        @media (min-width: 1024px) {
          .lc-grid { grid-template-columns: 1fr 1fr; }
        }

        /* ── Left panel ──
           --lc-on-panel is the luminance-picked readable color against
           secondary (theme textOnSecondary when AA-compliant, else white/black). */
        .lc-left {
          display: flex;
          justify-content: center;
          padding: 3rem 1.5rem;
          --lc-on: var(--lc-on-panel, var(--vibe-color-text-on-secondary));
        }
        @media (min-width: 768px) {
          .lc-left { padding: 4.5rem 3rem; }
        }
        .lc-inner {
          width: 100%;
          max-width: 640px;
        }

        .lc-heading {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 2.5rem;
          line-height: 1.1;
          color: var(--lc-on);
          text-align: center;
        }
        @media (min-width: 768px) {
          .lc-heading { font-size: 3rem; }
        }

        .lc-body {
          margin: 1.25rem auto 0;
          max-width: 34rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: color-mix(in srgb, var(--lc-on) 85%, transparent);
          text-align: center;
        }

        .lc-form-panel { margin-top: 2.5rem; }
        .lc-form-empty {
          font-family: var(--vibe-font-body);
          color: color-mix(in srgb, var(--lc-on) 70%, transparent);
          text-align: center;
        }

        /* ── Restyle the embedded lead-form block to the dark / underline look.
              The block uses inline styles, so these overrides use !important. ── */
        .lc-form-panel {
          --vibe-color-background: transparent;
          --vibe-radius-md: 0px;
          --vibe-radius-lg: 0px;
        }
        .lc-form-panel .mh-lead-form-card {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        /* Hide the block's own centered header/subheader — we render our own. */
        .lc-form-panel .mh-lead-form-card > h3,
        .lc-form-panel .mh-lead-form-card > p {
          display: none !important;
        }
        .lc-form-panel input,
        .lc-form-panel select,
        .lc-form-panel textarea {
          border: 0 !important;
          border-bottom: 1px solid color-mix(in srgb, var(--lc-on) 35%, transparent) !important;
          border-radius: 0 !important;
          background: transparent !important;
          color: var(--lc-on) !important;
          -webkit-text-fill-color: var(--lc-on) !important;
          font-family: var(--vibe-font-body) !important;
          padding-left: 0 !important;
          padding-right: 1.75rem !important;
        }
        /* Keep the empty date field's native value invisible so the fake
           placeholder span shows through. The block only renders that span
           when no date is selected, so :has is the reliable empty signal. */
        .lc-form-panel .mh-lead-form-date-field:has(> span[aria-hidden="true"]) input {
          color: transparent !important;
          -webkit-text-fill-color: transparent !important;
        }
        .lc-form-panel textarea { padding-right: 0 !important; }
        .lc-form-panel input:focus,
        .lc-form-panel select:focus,
        .lc-form-panel textarea:focus {
          border-bottom-color: var(--lc-cta-fill, var(--vibe-color-primary)) !important;
        }
        .lc-form-panel ::placeholder {
          color: color-mix(in srgb, var(--lc-on) 60%, transparent) !important;
          -webkit-text-fill-color: color-mix(in srgb, var(--lc-on) 60%, transparent) !important;
        }
        /* Desired Move-in Date fake placeholder — the block hardcodes #9ca3af
           on a <span>, which fails AA on dark secondaries. Override with the
           same luminance-picked muted on-panel color as ::placeholder. */
        .lc-form-panel .mh-lead-form-date-field > span[aria-hidden="true"] {
          color: color-mix(in srgb, var(--lc-on) 60%, transparent) !important;
          left: 0 !important;
          padding-right: 1.75rem !important;
        }
        .lc-form-panel .mh-lead-form-date-field svg,
        .lc-form-panel select + svg {
          color: color-mix(in srgb, var(--lc-on) 55%, transparent) !important;
        }
        .lc-form-panel select option { color: #111827; }
        /* Luminance-picked CTA fill: primary when it clears AA against the
           secondary panel, otherwise white/black so the button stays distinct. */
        /* Center the button within its full-width EditableSlotGroup wrapper.
           The button is inline-flex, so text-align on the wrapper centers it. */
        .lc-form-panel form > div:has(> .mh-lead-form-submit-button) {
          text-align: center;
        }
        .lc-form-panel .mh-lead-form-submit-button {
          /* Content-width like the CTA (inline-flex + px-10 py-4), centered. */
          width: auto !important;
          max-width: 100% !important;
          padding: 1rem 2.5rem !important;
          background-color: var(--lc-cta-fill, var(--vibe-color-primary)) !important;
          color: var(--lc-cta-text, var(--vibe-color-text-on-primary)) !important;
          font-family: var(--vibe-font-heading) !important;
          font-weight: 400 !important;
          font-size: 0.875rem !important;
          border-radius: 0 !important;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1) !important;
          transition: background-color 0.2s, color 0.2s !important;
          margin-top: 1rem;
        }
        .lc-form-panel .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: var(--lc-on) !important;
          color: var(--vibe-color-secondary) !important;
        }
        /* reCAPTCHA disclosure under the submit button. The block hardcodes
           #9ca3af on the paragraph and its links; swap to the luminance-picked
           on-panel color so small legal text still clears WCAG AA. */
        .lc-form-panel form > p,
        .lc-form-panel form > p a,
        .lc-form-panel form > p a:visited,
        .lc-form-panel form > p a:hover {
          color: var(--lc-on) !important;
        }

        /* ── Right map ── */
        .lc-right { position: relative; min-height: 360px; }
        .lc-map, .lc-map-placeholder {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 360px;
          border: none;
        }
        @media (min-width: 1024px) {
          .lc-right { min-height: 100%; }
          .lc-map, .lc-map-placeholder { position: absolute; inset: 0; height: 100%; }
        }
        .lc-map-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--vibe-color-surface, #f5f5f5);
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
        }

        .lc-sr-only {
          position: absolute;
          width: 1px; height: 1px;
          padding: 0; margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'LetsConnect',
  displayName: "Let's Connect (Form + Map)",
  tokens: {
    'letsconnect.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: "<p>Let's Connect</p>",
    },
    'letsconnect.body': {
      label: 'Intro Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>Have questions about how to start living at your new home? Our dedicated leasing team at <span data-type="field-token" data-field="property_name"></span> is here to assist you.</p>',
    },
    'letsconnect.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'letsconnect.map_heading': {
      label: 'Map Heading (screen-reader label)',
      type: 'text',
      defaultValue: 'Find Us',
    },
  },
};

(LetsConnect as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LetsConnect;
